import Axios from 'axios';
import { APP_VERSION } from '../version';
import { tokenManager } from '../utils/tokenManager';

export const AXIOS_INSTANCE = Axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '',
  withCredentials: true,
  paramsSerializer: {
    indexes: null, // serialize arrays as key=a&key=b (form-style exploded, matching oapi-codegen)
  },
});

// Track current app version
let currentVersion: string | null = null;
let versionChangeDispatched = false;
const VERSION_STORAGE_KEY = 'app-version';

// Dispatch version-changed event exactly once per page load
export function dispatchVersionChange(oldVersion: string, newVersion: string) {
  if (versionChangeDispatched) return;
  versionChangeDispatched = true;
  const event = new CustomEvent('app-version-changed', {
    detail: { oldVersion, newVersion }
  });
  window.dispatchEvent(event);
}

// Add request interceptor to include client version
// Note: Circuit breaker adds Authorization header before calling axios
AXIOS_INSTANCE.interceptors.request.use(async (config) => {
  const isBypass = (config as any).__circuitBreakerBypass;

  // Circuit breaker bypass requests: just add version header and proceed
  if (isBypass) {
    config.headers = config.headers || {};
    config.headers['X-Client-Version'] = APP_VERSION;
    return config;
  }

  // Non-bypass requests should never reach this point
  // They should be queued in customInstance() before calling axios
  throw new Error('Non-bypass request reached axios interceptor - this should not happen');
});

// Add response interceptor to handle version checking
AXIOS_INSTANCE.interceptors.response.use(
  (response) => {
    // Check for version header on every response
    const serverVersion = response.headers['x-app-version'];
    if (serverVersion) {
      // Initialize current version from storage on first response
      if (currentVersion === null) {
        currentVersion = localStorage.getItem(VERSION_STORAGE_KEY) || serverVersion;
        localStorage.setItem(VERSION_STORAGE_KEY, serverVersion);
      }

      // If version has changed, dispatch custom event
      if (currentVersion !== serverVersion) {
        dispatchVersionChange(currentVersion || APP_VERSION, serverVersion);
        localStorage.setItem(VERSION_STORAGE_KEY, serverVersion);
        currentVersion = serverVersion;
      }
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config as any;
    const isBypass = originalRequest.__circuitBreakerBypass;

    // Handle blob error responses - convert to JSON
    // When responseType is 'blob', error responses are also blobs, not parsed JSON
    if (error?.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        error.response.data = json; // Replace blob with parsed JSON
      } catch (e) {
        // If parsing fails, leave blob as-is
      }
    }

    // Bypass requests: skip all error handling, just return error as-is
    if (isBypass) {
      return Promise.reject(error);
    }

    // Non-bypass requests: handle 401 and 426
    const status = error?.response?.status;

    switch(status) {
    case 401:
      // auth error - attempt token refresh
      if (originalRequest._retry) {
        break;
      }

      originalRequest._retry = true;

      // Only attempt refresh if we have a refresh token
      const refreshToken = tokenManager.getRefreshToken();
      if (!refreshToken) {
        // No refresh token - user needs to log in
        // Don't call handleAuthError() to avoid redirect loop on login page
        break;
      }

      try {
        // Use TokenManager to refresh the token
        const newToken = await tokenManager.refreshToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return AXIOS_INSTANCE(originalRequest);
      } catch (refreshError) {
        // Token refresh failed, handle auth error
        tokenManager.handleAuthError();
        return Promise.reject(refreshError);
      }
    case 426:
      // Handle version mismatch (426 Upgrade Required)
      const serverVersion = error.response.headers['x-app-version'];
      if (serverVersion) {
        dispatchVersionChange(currentVersion || APP_VERSION, serverVersion);
        localStorage.setItem(VERSION_STORAGE_KEY, serverVersion);
        currentVersion = serverVersion;
      }
      break;
    }

    return Promise.reject(error);
  }
);
